import { Link } from 'react-router-dom';
import Button from '@/components/base/Button';
import { useSavedSubmission } from '@/hooks/useSavedSubmission';

const RESULT_KEY = 'kor38_supplier_insights';

const BENEFITS: string[] = [
  'No membership or subscription fees.',
  'Relevant introductions where opportunities arise.',
  'Referral commissions agreed and disclosed in advance.',
  'Suppliers remain responsible for their own bookings, contracts and delivery.',
];

export default function SupplierInsightsThankYou() {
  const { saved, ready } = useSavedSubmission(RESULT_KEY);

  return (
    <>
      <section className="w-full px-4 md:px-8 lg:px-12 pt-36 pb-14 md:pt-48 md:pb-20 bg-background-50">
        <div className="max-w-content mx-auto max-w-3xl">
          <span className="font-label text-xs uppercase tracking-widest2 text-primary-400">
            Supplier Insights
          </span>
          <h1 className="mt-5 font-heading text-3xl md:text-5xl leading-[1.1] text-foreground-950">
            Thank You for Sharing Your Insights
          </h1>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-foreground-700">
            Your responses will help us understand what event suppliers value and how KOR38 can
            create more meaningful business connections.
          </p>

          {ready ? (
            <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              {saved ? (
                saved.marketingOptIn ? (
                  <p className="flex items-start gap-3 text-base leading-relaxed text-foreground-800">
                    <i
                      className="ri-mail-check-line mt-0.5 text-xl text-primary-400"
                      aria-hidden="true"
                    ></i>
                    <span>
                      You have opted in to receive emails from KOR38. We&rsquo;ll be in touch with
                      supplier opportunities and relevant updates. You can unsubscribe at any time.
                    </span>
                  </p>
                ) : (
                  <p className="flex items-start gap-3 text-base leading-relaxed text-foreground-800">
                    <i
                      className="ri-mail-close-line mt-0.5 text-xl text-primary-400"
                      aria-hidden="true"
                    ></i>
                    <span>
                      You have not opted in to receive marketing emails from KOR38. Thank you for
                      contributing to our research. You can still apply to join our supplier network
                      using the button below.
                    </span>
                  </p>
                )
              ) : (
                <p className="flex items-start gap-3 text-base leading-relaxed text-foreground-600">
                  <i
                    className="ri-information-line mt-0.5 text-xl text-primary-400"
                    aria-hidden="true"
                  ></i>
                  <span>
                    We don&rsquo;t have a record of a survey submission on this device. If you
                    haven&rsquo;t taken part yet, you can still share your insights, or apply to join
                    the supplier network below.
                  </span>
                </p>
              )}
            </div>
          ) : null}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 pb-20 md:pb-28 bg-background-50">
        <div className="max-w-content mx-auto max-w-3xl">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
            <h2 className="font-heading text-2xl md:text-3xl leading-snug text-foreground-950">
              Could KOR38 Bring You Your Next Great Customer?
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              KOR38 personally connects customers with potentially suitable event suppliers. When a
              customer&rsquo;s needs match what you offer, we make a thoughtful introduction.
            </p>
            <ul className="mt-6 space-y-3">
              {BENEFITS.map((benefit) => (
                <li key={benefit} className="flex items-start gap-3 text-base leading-relaxed text-foreground-700">
                  <i
                    className="ri-checkbox-circle-line mt-0.5 text-lg text-primary-400"
                    aria-hidden="true"
                  ></i>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm leading-relaxed text-foreground-600">
              Introductions and bookings cannot be guaranteed, and joining the network does not
              guarantee any particular number of opportunities.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
              <Button to="/apply" variant="primary" size="lg" className="w-full sm:w-auto">
                Apply to join the KOR38 supplier network
              </Button>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-300 transition-colors duration-300"
              >
                <i className="ri-arrow-left-line" aria-hidden="true"></i>
                Back to home
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}